import { Entity } from 'playcanvas';

import { ElementAnimTrack } from './element-anim-track';
import { Element, ElementType } from './element';
import { Events } from './events';

type AnimatableElement = Element & {
    animationControllerEntity: Entity;
    animationPayloadEntity: Entity;
};

const isAnimatableElement = (element: Element | null | undefined): element is AnimatableElement => {
    if (!element || (element.type !== ElementType.splat && element.type !== ElementType.model)) {
        return false;
    }

    const candidate = element as Partial<AnimatableElement>;
    return !!candidate.animationControllerEntity && !!candidate.animationPayloadEntity;
};

class ElementAnimationRegistry {
    private readonly events: Events;
    private readonly tracks = new Map<Element, ElementAnimTrack>();
    private lastSampleTime: number | null = null;

    constructor(events: Events) {
        this.events = events;
    }

    private isAttachedToThisScene(element: Element | null | undefined) {
        return !!element?.scene && element.scene.events === this.events;
    }

    track(element: Element | null | undefined): ElementAnimTrack | null {
        if (!isAnimatableElement(element) || !this.isAttachedToThisScene(element)) {
            return null;
        }

        let track = this.tracks.get(element);
        if (!track) {
            track = new ElementAnimTrack(this.events, element);
            track.rebuild();
            this.tracks.set(element, track);
        }

        return track;
    }

    hasTrack(element: Element | null | undefined): boolean {
        return !!element && this.isAttachedToThisScene(element) && this.tracks.has(element);
    }

    allTracks(): ElementAnimTrack[] {
        return Array.from(this.tracks.values());
    }

    controller(element: Element | null | undefined): Entity | null {
        return isAnimatableElement(element) ? element.animationControllerEntity : null;
    }

    displayTarget(element: Element | null | undefined): Entity | null {
        return isAnimatableElement(element) ? element.animationPayloadEntity : null;
    }

    register(element: Element | null | undefined): ElementAnimTrack | null {
        return this.track(element);
    }

    unregister(element: Element | null | undefined) {
        if (!element) {
            return;
        }

        this.tracks.delete(element);
    }

    clear() {
        this.tracks.clear();
        this.lastSampleTime = null;
    }

    evaluateAll(frame: number) {
        this.lastSampleTime = frame;
        this.tracks.forEach((track, element) => {
            if (track.evaluate(frame)) {
                this.events.fire('elementAnim.applied', element, frame);
            }
        });
    }

    rebuildAll() {
        this.tracks.forEach((track) => {
            track.rebuild();
        });

        const sampleTime = this.lastSampleTime ?? this.events.invoke('timeline.frame');
        if (typeof sampleTime === 'number' && Number.isFinite(sampleTime)) {
            this.evaluateAll(sampleTime);
        }
    }
}

const registerElementAnimationEvents = (events: Events) => {
    const registry = new ElementAnimationRegistry(events);

    events.function('elementAnim.track', (element: Element) => {
        return registry.track(element);
    });

    events.function('elementAnim.hasTrack', (element: Element) => {
        return registry.hasTrack(element);
    });

    events.function('elementAnim.allTracks', () => {
        return registry.allTracks();
    });

    events.function('elementAnim.controller', (element: Element) => {
        return registry.controller(element);
    });

    events.function('elementAnim.displayTarget', (element: Element) => {
        return registry.displayTarget(element);
    });

    events.on('scene.elementAdded', (element: Element) => {
        registry.register(element);
    });

    events.on('scene.elementRemoved', (element: Element) => {
        registry.unregister(element);
    });

    events.on('scene.clear', () => {
        registry.clear();
    });

    events.on('timeline.frame', (frame: number) => {
        registry.evaluateAll(frame);
    });

    events.on('timeline.time', (time: number) => {
        registry.evaluateAll(time);
    });

    events.on('timeline.frames', () => {
        registry.rebuildAll();
    });

    events.on('timeline.smoothness', () => {
        registry.rebuildAll();
    });
};

export { registerElementAnimationEvents };